import { ProgressDetails, ProgressStatus } from 'core/components/progress/StepProgress'
import { useReducer, useCallback } from 'react'

/**
 * Enum for cluster operation progress steps
 */
export enum ProgressStep {
  Init = 'init',
  ControlPlane = 'controlPlane',
  NodeGroups = 'nodeGroups',
}

/**
 * Action types for the progress reducer
 */
export type ProgressAction =
  | { step: ProgressStep; status: ProgressStatus }
  | { step: 'reset' }
  | { step: 'setLabel'; stepNum: number; label: string }

const initialProgressState = (initLabel = '') => [
  { step: 1, status: ProgressStatus.Pending, label: initLabel },
  { step: 2, status: ProgressStatus.Pending, label: 'Initializing node groups' },
  { step: 3, status: ProgressStatus.Pending, label: 'Preparing infrastructure' },
]

const progressReducer = (state: ProgressDetails[], action: ProgressAction) => {
  if (action.step === 'setLabel') {
    return state.map((item) =>
      item.step === action.stepNum ? { ...item, label: action.label } : item,
    )
  }

  // Handle reset action
  if (action.step === 'reset') {
    return state.map((item) => ({ ...item, status: ProgressStatus.Pending }))
  }

  const stepMap: Record<ProgressStep, number> = {
    [ProgressStep.Init]: 1,
    [ProgressStep.ControlPlane]: 2,
    [ProgressStep.NodeGroups]: 3,
  }
  const currentStepNum = stepMap[action.step]

  return state.map((item) => {
    if (item.step < currentStepNum) return { ...item, status: ProgressStatus.Success }
    if (item.step === currentStepNum) return { ...item, status: action.status }
    if (action.status === ProgressStatus.Failed) return { ...item, status: ProgressStatus.Failed }
    return item
  })
}

/**
 * Custom hook for managing cluster operation progress tracking.
 *
 * This hook provides a reusable progress tracking pattern that:
 * - Manages three-step progress flow: Init → ControlPlane → NodeGroups
 * - Tracks status (pending, success, failed) for each step
 * - Supports cascading failures (when one step fails, all subsequent steps fail)
 * - Allows custom labels for each step
 * - Provides reset functionality to restart progress tracking
 *
 * The hook is designed for long-running cluster operations like:
 * - Cluster creation/deployment
 * - Node group addition
 * - Node group updates
 * - Cluster configuration changes
 *
 * @param {Object} config - Configuration object
 * @param {string} config.initLabel - Label for the initial step (e.g., "Setting Up Cluster")
 *
 * @returns {Object} Progress tracking utilities
 * @returns {Array} progressDetails - Array of progress step objects with step number, status, and label
 * @returns {Function} updateProgress - Function to update progress (step: ProgressStep, status: ProgressStatus) => void
 * @returns {Function} resetProgress - Function to reset all steps to pending status
 *
 * @example
 * ```tsx
 * const {
 *   progressDetails,
 *   updateProgress,
 *   resetProgress,
 * } = useClusterProgress({
 *   initLabel: 'Setting Up Cluster',
 * })
 *
 * // Update progress on successful steps
 * updateProgress(ProgressStep.Init, ProgressStatus.Success)
 * updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
 *
 * // Handle failures
 * updateProgress(ProgressStep.NodeGroups, ProgressStatus.Failed)
 *
 * // Reset on retry
 * resetProgress()
 *
 * // Display progress
 * <StepProgress
 *   progressDetails={progressDetails}
 *   title="Operation in progress..."
 * />
 * ```
 */
export const useClusterProgress = ({ initLabel }: { initLabel: string }) => {
  const [progressDetails, dispatchProgress] = useReducer(
    progressReducer,
    initialProgressState(initLabel),
  )

  const updateProgress = useCallback((step: ProgressStep, status: ProgressStatus) => {
    dispatchProgress({ step, status })
  }, [])

  const resetProgress = useCallback(() => {
    dispatchProgress({ step: 'reset' })
  }, [])

  return {
    progressDetails,
    updateProgress,
    resetProgress,
  }
}
